import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, ViewChild } from '@angular/core';
import { ModalService } from '@src/shared/modal/modal.service';
import getUnixTime from 'date-fns/getUnixTime';
import isBefore from 'date-fns/isBefore';
import subHours from 'date-fns/subHours';
import { roundDecimalError } from '@scripts/utils/math';
import { CategoryService } from '@scripts/woo_services.module/CategoryService';
import { FormatterService } from '@scripts/woo_services.module/FormatterService';
import { ExtraProductFormatField, ProductFormatService } from '@scripts/woo_services.module/ProductFormatService';
import { ProgramFormatService } from '@scripts/woo_services.module/ProgramFormatService';
import { PublisherService } from '@scripts/woo_services.module/PublisherService';
import { TranslationService } from '@scripts/woo_services.module/TranslationService';
import {
  Category,
  OmsProgramCatalog,
  ProductFormats,
  Program,
  ProgramSegment,
  ProgramShares,
  Publisher,
  wooId,
} from '@scripts/woo_services.module/shared-types';

// The backend flags the catalog as stale after this long without a successful sync.
const OMS_CATALOG_STALE_HOURS = 6;

@Component({
  selector: 'manage-program-formats',
  templateUrl: './manage-program-formats.component.html',
  styleUrls: ['../../../shared/table.component.scss', 'manage-program-formats.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ManageProgramFormats {
  @Input('headerPos') parentHeader: HTMLElement;
  @ViewChild('newItemProgramFormat', { static: true }) newItemProgramFormat: ElementRef;

  readonly inputFieldIds = {
    prerollLongform: 'preroll-longform',
    prerollShortform: 'preroll-shortform',
    midrollLongform: 'midroll-longform',
    midrollShortform: 'midroll-shortform',
  };

  constructor(
    private categoryService: CategoryService,
    public formatterService: FormatterService,
    private programFormatService: ProgramFormatService,
    private publisherService: PublisherService,
    private productFormatService: ProductFormatService,
    private modalService: ModalService,
    private translationService: TranslationService,
    private changeDetection: ChangeDetectorRef,
  ) {}

  show = {
    newProgramFormatForm: false,
    estimatesHaveChanged: false,
    updatingEstimates: false,
    loading: true,
    isEditing: false,
  };

  programFormats: Program[] = [];
  publishers: Publisher[] = [];
  categories: Category[] = [];
  selectedCategory: Category;
  cachedPrograms: Program[] = [];
  selectedRow: wooId[] = [];
  selectedRowForEditing: wooId = null;
  showMidroll = false;
  longformId: string;
  shortformId: string;
  omsCatalog: OmsProgramCatalog = null;
  omsCatalogStale = false;

  async ngOnInit(): Promise<void> {
    void this.loadOmsCatalog();
    const [publishers, categories, programFormats, productFormats] = await Promise.all([
      this.publisherService.getPublishers(),
      this.categoryService.getCategories(),
      this.programFormatService.getProgramFormats(),
      this.productFormatService.getProductFormats(ExtraProductFormatField.enabled, ExtraProductFormatField.format),
    ]);
    try {
      this.publishers = publishers;
      this.categories = categories;
      this.programFormats = programFormats;

      this.cachedPrograms = this.deepCopyProgramFormats(programFormats);

      const availableFormats = productFormats.filter((pf) => pf.enabled);
      this.longformId = availableFormats.find((pf) => pf.format === ProductFormats.longForm).id;
      this.shortformId = availableFormats.find((pf) => pf.format === ProductFormats.shortForm).id;
      this.changeDetection.markForCheck();
    } finally {
      this.show.loading = false;
    }
  }

  private loadOmsCatalog(): Promise<void> {
    return (
      this.programFormatService
        .getOmsCatalog()
        .then((catalog) => {
          this.omsCatalog = catalog;
          // A never-synced catalog has its own message, so it is not also flagged as stale.
          this.omsCatalogStale =
            !!catalog.synced_at && isBefore(new Date(catalog.synced_at), subHours(new Date(), OMS_CATALOG_STALE_HOURS));
          this.changeDetection.markForCheck();
        })
        // The global error handler already shows the failure; the status line just stays hidden.
        .catch(() => undefined)
    );
  }

  autoFill(programFormatShares: ProgramShares[], inputFieldId: string): void {
    if (inputFieldId === this.inputFieldIds.prerollLongform) {
      this.programShareShortform(programFormatShares).preroll = roundDecimalError(
        1 - this.programShareLongform(programFormatShares).preroll,
        3,
      );
    } else if (inputFieldId === this.inputFieldIds.prerollShortform) {
      this.programShareLongform(programFormatShares).preroll = roundDecimalError(
        1 - this.programShareShortform(programFormatShares).preroll,
        3,
      );
    } else if (inputFieldId === this.inputFieldIds.midrollLongform) {
      this.programShareShortform(programFormatShares).midroll = roundDecimalError(
        1 - this.programShareLongform(programFormatShares).midroll,
        3,
      );
    } else if (inputFieldId === this.inputFieldIds.midrollShortform) {
      this.programShareLongform(programFormatShares).midroll = roundDecimalError(
        1 - this.programShareShortform(programFormatShares).midroll,
        3,
      );
    }
  }

  async toggleActiveFormat(format: Program): Promise<void> {
    await this.programFormatService.updateProgramFormat(format, format.product_format_program_share);
  }

  async save(formatId: wooId = null): Promise<void> {
    const filterActive = this.selectedCategory;
    const textBlocks = ['Är du säker på att du vill spara dina ändringar?'];
    if (filterActive) {
      textBlocks.push('Notera att även ändringar i filtrerade rader sparas.');
    }
    await this.modalService
      .openConfirm({
        title: 'Bekräfta ändringar',
        texts: textBlocks,
        confirmLabel: 'Spara',
        cancelLabel: 'Avbryt',
      })
      .then(async (status) => {
        if (status.confirm) {
          const loaderModal = this.modalService.openLoader('Sparar ändringar');

          this.show.updatingEstimates = true;
          if (formatId) {
            const programFormat = this.programFormats.find((pf) => pf.id === formatId);
            programFormat.oms_name = programFormat.oms_name?.trim() || null;
            const shares = programFormat.product_format_program_share;
            this.updateDerivedFields(await this.programFormatService.updateProgramFormat(programFormat, shares));
            this.cachedPrograms = this.deepCopyProgramFormats(this.programFormats);
          } else {
            const allShares = this.programFormats.flatMap((program) => program.product_format_program_share);
            this.productFormatService
              .updateProgramFormatShares(allShares)
              .then((updatedShares) => {
                this.programFormats.map((pf) => {
                  pf.product_format_program_share = updatedShares.filter(
                    (shares) => shares.program_format_id === pf.id,
                  );
                });
                this.cachedPrograms = this.deepCopyProgramFormats(this.programFormats);
                this.show.estimatesHaveChanged = true;
                loaderModal.close();
              })
              .catch((err) => {
                loaderModal.close();
                this.programFormats = this.cachedPrograms;
                this.changeDetection.markForCheck();

                this.modalService.openError(this.translationService.convertError(err.error.error));
              })
              .finally(() => {
                loaderModal.close();
                this.setEditingDistribution(false);
                this.setEditingDetails(false, this.selectedRowForEditing);
                this.selectedRowForEditing = null;
                this.show.updatingEstimates = false;
                this.changeDetection.markForCheck();
              });
          }
        }
      })
      .finally(() => {
        this.cancelEditing();
        this.modalService.closeAll();
        this.changeDetection.markForCheck();
      });
  }

  rowVisible(obj: Program): boolean {
    const categoryFiltered = this.selectedCategory && obj.category_id !== this.selectedCategory.id;
    return !categoryFiltered;
  }

  setSelected(programId: wooId): void {
    if (this.selectedRow.includes(programId)) {
      this.selectedRow = this.selectedRow.filter((row) => !(row === programId));
    } else {
      this.selectedRow.push(programId);
    }
  }

  openTab(obj: Program): boolean {
    return this.selectedRow.includes(obj.id);
  }

  setEditingDistribution(value: boolean): void {
    this.show.isEditing = value;
    this.selectedRowForEditing = null;
  }

  setEditingDetails(value: boolean, id: wooId): void {
    this.selectedRowForEditing = id;
    this.show.isEditing = false;
  }

  isEditing(formatId: wooId): boolean {
    return formatId === this.selectedRowForEditing;
  }

  cancelEditing(): void {
    this.programFormats = this.deepCopyProgramFormats(this.cachedPrograms);
    this.setEditingDetails(false, this.selectedRowForEditing);
    this.selectedRowForEditing = null;
    this.setEditingDistribution(false);
  }

  openCard(type: string): void {
    if (type === 'program') {
      this.show.newProgramFormatForm = true;
    }
  }

  addNewFormat(format: Program): void {
    this.programFormats.push(format);
  }

  closeCard(): void {
    this.show.newProgramFormatForm = false;
  }

  filterPeriods(periods: ProgramSegment[], compareTime?: Date): ProgramSegment[] {
    const time = compareTime ? compareTime : new Date();
    const filtered = periods.filter((period: ProgramSegment) => {
      return isBefore(time, new Date(period.end_date));
    });

    const lastPeriod = this.maxBy(periods, (period: ProgramSegment) => {
      return getUnixTime(new Date(period.end_date));
    });
    return filtered.length > 0 ? filtered : [lastPeriod];
  }

  maxBy(array: any, callback: any): ProgramSegment {
    const max = Math.max(...array.map(callback));
    return array.find((item) => callback(item) === max);
  }

  programShareLongform(share: ProgramShares[]): ProgramShares {
    return share.find((ps) => ps.product_format_id === this.longformId);
  }

  programShareShortform(share: ProgramShares[]): ProgramShares {
    return share.find((ps) => ps.product_format_id === this.shortformId);
  }

  // Fields the backend derives from the saved format; an OMS alias can change whether it is bookable.
  private updateDerivedFields(updatedProgramFormat: Program): void {
    const programFormat = this.programFormats.find((format) => format.id === updatedProgramFormat.id);
    programFormat.category_name = updatedProgramFormat.category_name;
    programFormat.oms_bookable = updatedProgramFormat.oms_bookable;
  }
  private deepCopyProgramFormats(programFormats: Program[]): Program[] {
    return JSON.parse(JSON.stringify(programFormats));
  }
}
